import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type UIEvent as ReactUIEvent,
} from 'react'
import { toast } from 'sonner'
import { useEditSession } from '@/core/store/editSession'
import { TEST_IDS } from '@/lib/testIds'
import { useIsCoarsePointer } from '@/lib/usePointerType'
import { cn } from '@/lib/utils'
import { Button } from '@/ui/shadcn/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/ui/shadcn/dialog'
import { BitmapCanvas } from '@/tools/quality/BitmapCanvas'
import { windowRectAround } from '@/tools/quality/qualityWindow'
import {
  startQualityWorkerSession,
  type QualityWorkerSession,
} from '@/tools/quality/qualityWorker'
import type { QualityWindowEncodedResponse } from '@/core/export/protocol'
import { formatBytes } from '@/core/export'
import {
  cacheExactQualitySizes,
  useExactQualitySizes,
} from '@/tools/quality/qualitySizeCache'

const DEFAULT_SWIPE_DIVIDER_PERCENT = 50
const CANVAS_REENCODE_LIMIT_MESSAGE = 'Canvas re-encode limit reached — see Decisions #2'
export const QUALITY_FULL_ENCODE_DEBOUNCE_MS = 600

function exactBytes(bytes: number): string {
  return formatBytes(bytes).replace(/^~/, '')
}

function closeWindowResult(result: QualityWindowEncodedResponse | null): void {
  result?.original.close()
  result?.jpeg.close()
  result?.difference.close()
}

function LoupePane({
  bitmap,
  label,
  testId,
  onScroll,
  paneRef,
}: {
  bitmap: ImageBitmap
  label: string
  testId: string
  onScroll: (event: ReactUIEvent<HTMLDivElement>) => void
  paneRef: React.RefObject<HTMLDivElement | null>
}) {
  return (
    <div className="min-w-0">
      <p className="mb-1.5 text-center text-xs font-medium">{label}</p>
      <div ref={paneRef} onScroll={onScroll} className="max-h-[55dvh] overflow-auto rounded-md border bg-black">
        <BitmapCanvas
          bitmap={bitmap}
          ariaLabel={`${label} quality window at 100 percent`}
          testId={testId}
          className="block max-w-none [image-rendering:pixelated]"
          style={{ width: bitmap.width, height: bitmap.height }}
        />
      </div>
    </div>
  )
}

export function QualityOverlay() {
  const document = useEditSession((state) => state.document)
  const sources = useEditSession((state) => state.sources)
  const exportOptions = useEditSession((state) => state.exportOptions)
  const qualityViewOpen = useEditSession((state) => state.qualityViewOpen)
  const qualityLoupePoint = useEditSession((state) => state.qualityLoupePoint)
  const qualityDiffVisible = useEditSession((state) => state.qualityDiffVisible)
  const setQualityViewOpen = useEditSession((state) => state.setQualityViewOpen)
  const setQualityLoupePoint = useEditSession((state) => state.setQualityLoupePoint)
  const setQualityDiffVisible = useEditSession((state) => state.setQualityDiffVisible)
  const coarsePointer = useIsCoarsePointer()
  const [session, setSession] = useState<QualityWorkerSession | null>(null)
  const [windowResult, setWindowResult] = useState<QualityWindowEncodedResponse | null>(null)
  const [renderError, setRenderError] = useState<Error | null>(null)
  const [swipeDivider, setSwipeDivider] = useState(DEFAULT_SWIPE_DIVIDER_PERCENT)
  const [holdOriginal, setHoldOriginal] = useState(false)
  const navigatorPointerId = useRef<number | null>(null)
  const swipePaneRef = useRef<HTMLDivElement>(null)
  const originalPaneRef = useRef<HTMLDivElement>(null)
  const jpegPaneRef = useRef<HTMLDivElement>(null)
  const synchronizingScroll = useRef(false)
  const resizeMode = exportOptions.resizeMode
  const customLongestSide = exportOptions.customLongestSide
  const exactSizes = useExactQualitySizes(document, sources, exportOptions)

  useEffect(() => {
    if (!qualityViewOpen || !document) return
    let cancelled = false
    let activeSession: QualityWorkerSession | null = null
    const options = useEditSession.getState().exportOptions
    void startQualityWorkerSession(sources, document, options).then((created) => {
      activeSession = created
      if (cancelled) created.dispose()
      else setSession(created)
    }).catch((error: unknown) => {
      if (!cancelled) setRenderError(error instanceof Error ? error : new Error('Quality render failed'))
    })
    return () => {
      cancelled = true
      activeSession?.dispose()
    }
    // The quality value changes window encoding, not the cached output render.
  }, [customLongestSide, document, qualityViewOpen, resizeMode, sources])

  useEffect(() => {
    if (!session) return
    let cancelled = false
    const point = qualityLoupePoint ?? {
      x: session.outputSize.width / 2,
      y: session.outputSize.height / 2,
    }
    void session.encodeWindow(point, exportOptions.jpegQuality).then((result) => {
      if (cancelled) {
        closeWindowResult(result)
        return
      }
      setWindowResult((previous) => {
        closeWindowResult(previous)
        return result
      })
    }).catch((error: unknown) => {
      if (!cancelled) setRenderError(error instanceof Error ? error : new Error('Quality window encode failed'))
    })
    return () => {
      cancelled = true
    }
  }, [exportOptions.jpegQuality, qualityLoupePoint, session])

  useEffect(() => () => closeWindowResult(windowResult), [windowResult])

  useEffect(() => {
    if (!session || !document || exactSizes) return
    let cancelled = false
    const timeoutId = window.setTimeout(() => {
      void session.encodeFull(exportOptions.jpegQuality).then((result) => {
        if (!cancelled) cacheExactQualitySizes(document, sources, exportOptions, result)
      }).catch((error: unknown) => {
        if (!cancelled) setRenderError(error instanceof Error ? error : new Error('Full quality encode failed'))
      })
    }, QUALITY_FULL_ENCODE_DEBOUNCE_MS)
    return () => {
      cancelled = true
      window.clearTimeout(timeoutId)
    }
  }, [document, exactSizes, exportOptions, session, sources])

  if (!document) {
    if (qualityViewOpen) throw new Error('Quality view cannot open without a document')
    return null
  }
  if (renderError) throw renderError

  const outputSize = session?.outputSize
  const selectedRect = outputSize
    ? windowResult?.rect ?? windowRectAround(qualityLoupePoint ?? {
        x: outputSize.width / 2,
        y: outputSize.height / 2,
      }, outputSize)
    : null

  const pickLoupePoint = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!session) throw new Error('Quality navigator selection requires a rendered session')
    const bounds = event.currentTarget.getBoundingClientRect()
    if (bounds.width <= 0 || bounds.height <= 0) throw new Error('Quality navigator requires measurable bounds')
    setQualityLoupePoint({
      x: Math.min(session.outputSize.width, Math.max(0, (event.clientX - bounds.left) / bounds.width * session.outputSize.width)),
      y: Math.min(session.outputSize.height, Math.max(0, (event.clientY - bounds.top) / bounds.height * session.outputSize.height)),
    })
  }

  const syncScroll = (source: HTMLDivElement, target: HTMLDivElement | null) => {
    if (!target || synchronizingScroll.current) return
    synchronizingScroll.current = true
    target.scrollLeft = source.scrollLeft
    target.scrollTop = source.scrollTop
    requestAnimationFrame(() => {
      synchronizingScroll.current = false
    })
  }

  const updateSwipeDivider = (clientX: number) => {
    const pane = swipePaneRef.current
    if (!pane) throw new Error('Quality swipe comparison pane is missing')
    const bounds = pane.getBoundingClientRect()
    if (bounds.width <= 0) throw new Error('Quality swipe comparison pane requires measurable bounds')
    setSwipeDivider(Math.min(100, Math.max(0, (clientX - bounds.left) / bounds.width * 100)))
  }

  const toggleDifference = () => {
    const visible = !qualityDiffVisible
    setQualityDiffVisible(visible)
    if (visible && exportOptions.jpegQuality === 1) toast.info(CANVAS_REENCODE_LIMIT_MESSAGE)
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSession(null)
      setRenderError(null)
      setWindowResult((current) => {
        closeWindowResult(current)
        return null
      })
    }
    setQualityViewOpen(open)
  }

  return (
    <Dialog open={qualityViewOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid={TEST_IDS.qualityOverlay}
        className={cn(
          'flex h-[min(92dvh,900px)] max-w-[1120px] flex-col gap-0 overflow-hidden p-0',
          coarsePointer && 'h-dvh max-h-none w-screen max-w-none rounded-none border-0',
        )}
      >
        <DialogHeader className="border-b px-5 py-4 text-left">
          <DialogTitle>Compare JPEG quality</DialogTitle>
          <DialogDescription>Inspect the final export at 100%. Pick an area in the navigator, then compare its exact pixels.</DialogDescription>
        </DialogHeader>
        {!session || !windowResult ? (
          <div className="grid min-h-0 flex-1 place-items-center bg-muted/30 p-6 text-sm text-muted-foreground" data-testid={TEST_IDS.qualityLoading}>Rendering final export…</div>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            <div className="mx-auto flex max-w-[1040px] flex-col gap-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm tabular-nums" data-testid={TEST_IDS.qualityWindowBytes} data-quality-window-bytes={windowResult.bytes}>
                  q {exportOptions.jpegQuality.toFixed(2)} · window {windowResult.rect.width}×{windowResult.rect.height} · JPEG window {windowResult.bytes.toLocaleString()} bytes
                </p>
                <Button type="button" size="sm" variant={qualityDiffVisible ? 'default' : 'outline'} aria-pressed={qualityDiffVisible} data-testid={TEST_IDS.qualityDiffToggle} onClick={toggleDifference}>
                  Difference ×8
                </Button>
              </div>
              <p className="text-sm tabular-nums text-muted-foreground" data-testid={TEST_IDS.qualityWholeFile}>
                {exactSizes
                  ? `Whole file: ${exactBytes(exactSizes.jpegBytes)} (real) · PNG: ${exactBytes(exactSizes.pngBytes)}`
                  : 'Encoding whole file and PNG reference…'}
              </p>

              {coarsePointer ? (
                <div>
                  <div className="max-h-[55dvh] overflow-auto rounded-md border bg-black">
                    <div ref={swipePaneRef} className="relative" style={{ width: windowResult.rect.width, height: windowResult.rect.height }}>
                      {qualityDiffVisible ? (
                        <BitmapCanvas bitmap={windowResult.difference} ariaLabel="Amplified JPEG difference" testId={TEST_IDS.qualityDifference} className="absolute inset-0 [image-rendering:pixelated]" />
                      ) : (
                        <>
                          <BitmapCanvas bitmap={windowResult.original} ariaLabel="Uncompressed quality window" testId={TEST_IDS.qualityLoupeOriginal} className="absolute inset-0 [image-rendering:pixelated]" />
                          {!holdOriginal ? (
                            <BitmapCanvas
                              bitmap={windowResult.jpeg}
                              ariaLabel={`JPEG quality ${exportOptions.jpegQuality.toFixed(2)} window`}
                              testId={TEST_IDS.qualityLoupeJpeg}
                              className="absolute inset-0 [image-rendering:pixelated]"
                              style={{ clipPath: `inset(0 0 0 ${swipeDivider}%)` }}
                            />
                          ) : null}
                          <button
                            type="button"
                            className="absolute inset-y-0 z-10 w-8 -translate-x-1/2 touch-none cursor-ew-resize before:absolute before:inset-y-0 before:left-1/2 before:w-0.5 before:bg-white before:shadow"
                            style={{ left: `${swipeDivider}%` }}
                            data-testid={TEST_IDS.qualitySwipeDivider}
                            aria-label="Move comparison divider"
                            onPointerDown={(event) => {
                              event.currentTarget.setPointerCapture(event.pointerId)
                              updateSwipeDivider(event.clientX)
                            }}
                            onPointerMove={(event) => {
                              if (event.currentTarget.hasPointerCapture(event.pointerId)) updateSwipeDivider(event.clientX)
                            }}
                            onPointerUp={(event) => {
                              updateSwipeDivider(event.clientX)
                              if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId)
                            }}
                          />
                        </>
                      )}
                    </div>
                  </div>
                  {!qualityDiffVisible ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-2 w-full select-none"
                      onPointerDown={() => setHoldOriginal(true)}
                      onPointerUp={() => setHoldOriginal(false)}
                      onPointerCancel={() => setHoldOriginal(false)}
                      onPointerLeave={() => setHoldOriginal(false)}
                    >
                      Hold to show uncompressed
                    </Button>
                  ) : null}
                </div>
              ) : (
                <div className="grid min-w-0 grid-cols-2 gap-3">
                  <LoupePane
                    bitmap={windowResult.original}
                    label="Uncompressed"
                    testId={TEST_IDS.qualityLoupeOriginal}
                    paneRef={originalPaneRef}
                    onScroll={(event) => syncScroll(event.currentTarget, jpegPaneRef.current)}
                  />
                  <LoupePane
                    bitmap={qualityDiffVisible ? windowResult.difference : windowResult.jpeg}
                    label={qualityDiffVisible ? 'Difference ×8' : `JPEG q=${exportOptions.jpegQuality.toFixed(2)}`}
                    testId={qualityDiffVisible ? TEST_IDS.qualityDifference : TEST_IDS.qualityLoupeJpeg}
                    paneRef={jpegPaneRef}
                    onScroll={(event) => syncScroll(event.currentTarget, originalPaneRef.current)}
                  />
                </div>
              )}

              <div className="mx-auto w-full max-w-[360px]">
                <p className="mb-1.5 text-xs font-medium">Pick an area</p>
                <div
                  className="relative touch-none overflow-hidden rounded-md border bg-black"
                  onPointerDown={(event) => {
                    navigatorPointerId.current = event.pointerId
                    event.currentTarget.setPointerCapture(event.pointerId)
                    pickLoupePoint(event)
                  }}
                  onPointerMove={(event) => {
                    if (navigatorPointerId.current === event.pointerId) pickLoupePoint(event)
                  }}
                  onPointerUp={(event) => {
                    if (navigatorPointerId.current === event.pointerId) navigatorPointerId.current = null
                    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId)
                  }}
                  onPointerCancel={() => {
                    navigatorPointerId.current = null
                  }}
                >
                  <BitmapCanvas bitmap={session.navigator} ariaLabel="Quality navigator" testId={TEST_IDS.qualityNavigator} className="block h-auto w-full" />
                  {selectedRect && outputSize ? (
                    <span
                      className="pointer-events-none absolute border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.65)]"
                      style={{
                        left: `${selectedRect.x / outputSize.width * 100}%`,
                        top: `${selectedRect.y / outputSize.height * 100}%`,
                        width: `${selectedRect.width / outputSize.width * 100}%`,
                        height: `${selectedRect.height / outputSize.height * 100}%`,
                      }}
                    />
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
